import React, { useEffect } from "react";
import { RootRouteProps } from "routers/types/stack";
import { useRoute, useIsFocused } from "@react-navigation/native";

import { View, ScrollView } from "react-native";
import {
  Input,
  Picker,
  Button,
  TextArea,
  InputLabel,
  ConfirmAlert,
  RadioButtonGroup,
  DeleteAlertComponent,
} from "components";
import Addresses from "./Addresses";
import CoWorkers from "./CoWorkers";
import PopularAttrs from "./PopularAttrs";
import PopularProducts from "./PopularProducts";

import { colors, patterns, sizes, strings } from "assets/constants";
import css from "styles";
import { mq, sn } from "utils";

import { useForm, Controller } from "react-hook-form";
import {
  useEditCustomer,
  useDeleteCustomer,
  useCustomerCategories,
  useCustomerBaseOptions,
} from "clients/apis";
import { getPostData } from "../helper";

import { useRecoilValue } from "recoil";
import { authAtom } from "stores";
import { useChmod, useFlag } from "hooks";
import PopularCategories from "./PopularCategories";
import { _goToBack } from "routers/helper";

const sharerData = [
  { id: 0, title: strings.does_not_have },
  { id: 1, title: strings.hasIt },
];

export default function BaseInfo() {
  const { chmod } = useChmod();

  const isFocused = useIsFocused();

  const { role, id: userId } = useRecoilValue(authAtom);

  const { data } = useRoute<RootRouteProps<"customerTabs">>().params;
  const { data: customerCategories } = useCustomerCategories();
  const { data: customerBaseOptions } = useCustomerBaseOptions();
  const { flg: alert, on: showAlert, off: hideAlert } = useFlag();

  const hasEditPermission =
    chmod("customer", "PUT") &&
    (role === strings.ceo || data?.admin_id === userId);

  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors: inputErrors },
  } = useForm<CustomerFormDataT>({
    // defaultValues: {
    //   name: "",
    //   mobile: "",
    //   status: null,
    //   category: null,
    //   familiarityWay: null,
    //   coWorkers: null,
    //   addresses: null,
    //   personalCharacteristics: "",
    //   shopName: "",
    //   shopOwner: null, // ------
    //   area: "",
    //   rentalTime: "",
    //   sharer: null, // ----
    //   branchCount: "",
    //   employeeCount: "",
    //   regalCount: "",
    //   workTime: "",
    //   role: "",
    //   otherJob: "",
    //   shopDescriptions: "",
    //   popularProducts: null,
    //   popularCategories: null,
    //   popularAttrs: null,
    // },
  });

  useEffect(() => {
    if (data && isFocused) {
      setValue("name", data?.name);
      setValue("mobile", data?.mobile);
      setValue("status", data?.status);
      setValue("category", data?.category);
      setValue("familiarityWay", data?.frindship);
      setValue("coWorkers", data?.colleagues);
      setValue("addresses", data?.addresses);
      setValue("personalCharacteristics", data?.characteristics);
      setValue("shopName", data?.shop?.name);
      setValue("shopOwner", data?.shop?.owner_type); // ------
      setValue("area", data?.shop?.area);
      setValue("rentalTime", data?.shop?.rental_period);
      setValue("sharer", sharerData[data?.shop?.has_partner]); // ----
      setValue("branchCount", data?.shop?.branch);
      setValue("employeeCount", data?.shop?.employee);
      setValue("regalCount", data?.shop?.regal);
      setValue("workTime", data?.shop?.activity_duration);
      setValue("role", data?.job);
      setValue("otherJob", data?.shop?.other_jobs);
      setValue("shopDescriptions", data?.shop?.description);
      setValue("popularProducts", data?.shop?.popular_products);
      setValue("popularCategories", data?.shop?.popular_categories);
      setValue("popularAttrs", data?.shop?.popular_attributes);
    }
  }, [data, setValue, isFocused]);

  const editCustomerMut = useEditCustomer();
  const deleteCustomerMut = useDeleteCustomer();

  const _onEdit = () => {
    editCustomerMut.mutate(
      {
        id: data?.id,
        data: { ...getPostData(getValues(), data?.id), section: "basic" },
      },
      { onSuccess: _goToBack }
    );
  };

  const _onDelete = (close: () => void) =>
    deleteCustomerMut.mutate(data?.id, { onSuccess: close });

  return (
    <>
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={css.responsiveInputsListContainer}
      >
        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.name} />
          <Controller
            name="name"
            control={control}
            rules={{
              required: {
                value: true,
                message: strings.customer_name_is_required,
              },
            }}
            render={({ field: { value, onChange } }) => (
              <Input
                editable={hasEditPermission}
                placeholder={strings.insert_customer_name}
                icon={{ name: "user" }}
                onChangeText={onChange}
                value={value}
                error={inputErrors.name?.message}
              />
            )}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <>
            <InputLabel label={strings.mobile} />
            <Controller
              name="mobile"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: strings.customer_mobile_is_required,
                },
              }}
              render={({ field: { value, onChange } }) => (
                <Input
                  editable={hasEditPermission}
                  placeholder={strings.insert_customer_mobile}
                  icon={{ name: "smartphone" }}
                  onChangeText={onChange}
                  value={value}
                  error={inputErrors.mobile?.message}
                  keyboardType="phone-pad"
                />
              )}
            />
          </>
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.customer_type} />
          {customerBaseOptions?.data?.status && (
            <Controller
              name="status"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Picker
                  data={customerBaseOptions?.data?.status}
                  value={value?.title}
                  onSelect={onChange}
                  icon={{ name: "list" }}
                  disabled={!hasEditPermission}
                />
              )}
            />
          )}
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.customer_category} />
          <Controller
            name="category"
            control={control}
            render={({ field: { value, onChange } }) => {
              return (
                <Picker
                  disabled={!hasEditPermission}
                  onSelect={onChange}
                  value={value?.title ? value?.title : undefined}
                  data={customerCategories?.data?.data}
                  icon={{ name: "grid" }}
                  placeholder={strings.pick_customer_category}
                />
              );
            }}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.familiarity_way} />
          <Controller
            name="familiarityWay"
            control={control}
            render={({ field: { value, onChange } }) => {
              return (
                <Picker
                  disabled={!hasEditPermission}
                  onSelect={onChange}
                  value={value?.title ? value?.title : undefined}
                  data={
                    customerBaseOptions?.data?.frindship?.original?.data?.items
                  }
                  icon={{ name: "airplay" }}
                  placeholder={strings.pick_customer_familiarity_way}
                />
              );
            }}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.co_workers} />
          <Controller
            name="coWorkers"
            control={control}
            render={({ field: { value, onChange } }) => {
              return (
                <CoWorkers
                  disabled={!hasEditPermission}
                  value={value}
                  onChange={onChange}
                />
              );
            }}
          />
        </View>

        <View style={css.fitWidth}>
          <InputLabel label={strings.addresses} />
          <Controller
            name="addresses"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Addresses
                disabled={!hasEditPermission}
                onChange={onChange}
                addresses={value}
              />
            )}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.personal_characteristics} />
          <Controller
            name="personalCharacteristics"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Input
                editable={hasEditPermission}
                placeholder={strings.insert_personal_characteristics}
                icon={{ name: "edit-2" }}
                onChangeText={onChange}
                value={value}
              />
            )}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.shop_name} />
          <Controller
            name="shopName"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Input
                editable={hasEditPermission}
                placeholder={strings.insert_shop_name}
                icon={{ name: "edit-2" }}
                onChangeText={onChange}
                value={value}
              />
            )}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <View
            style={{
              ...css.rRowCenterAlign,
              marginBottom: sizes.inputsBetweenSpace,
            }}
          >
            <InputLabel removedSpace label={strings.shop_owner} desc=" :" />
            <Controller
              name="shopOwner"
              control={control}
              render={({ field: { value, onChange } }) => (
                <RadioButtonGroup
                  disabled={!hasEditPermission}
                  value={value}
                  selectedItem={onChange}
                  data={customerBaseOptions?.data?.ownerShop || []}
                />
              )}
            />
          </View>
        </View>

        <View style={css.responsiveInputContainer}>
          <View
            style={{
              ...css.rRowCenterAlign,
              marginBottom: sizes.inputsBetweenSpace,
            }}
          >
            <InputLabel removedSpace label={strings.sharer} desc=" :" />
            <Controller
              name="sharer"
              control={control}
              render={({ field: { value, onChange } }) => (
                <RadioButtonGroup
                  disabled={!hasEditPermission}
                  value={value}
                  selectedItem={onChange}
                  data={sharerData}
                />
              )}
            />
          </View>
        </View>

        <View style={{ height: mq(20) }} />

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.area} desc={` (${strings.m2})`} />
          <Controller
            name="area"
            rules={{
              pattern: {
                value: patterns.number,
                message: strings.number_type_error,
              },
            }}
            control={control}
            render={({ field: { value, onChange } }) => (
              <Input
                editable={hasEditPermission}
                placeholder={strings.area_sample}
                icon={{ name: "edit-2" }}
                onChangeText={onChange}
                value={value ? String(value) : undefined}
                keyboardType="number-pad"
                error={inputErrors.area?.message}
              />
            )}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel
            label={strings.rental_time}
            desc={` (${strings.year_count})`}
          />
          <Controller
            name="rentalTime"
            rules={{
              pattern: {
                value: patterns.number,
                message: strings.number_type_error,
              },
            }}
            control={control}
            render={({ field: { value, onChange } }) => (
              <Input
                editable={hasEditPermission}
                placeholder={strings.rental_sample}
                icon={{ name: "edit-2" }}
                onChangeText={onChange}
                value={value ? String(value) : undefined}
                keyboardType="number-pad"
                error={inputErrors.rentalTime?.message}
              />
            )}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.branch_count} />
          <Controller
            name="branchCount"
            rules={{
              pattern: {
                value: patterns.number,
                message: strings.number_type_error,
              },
            }}
            control={control}
            render={({ field: { value, onChange } }) => (
              <Input
                editable={hasEditPermission}
                placeholder={strings.branch_count_sample}
                icon={{ name: "edit-2" }}
                onChangeText={onChange}
                value={value ? String(value) : undefined}
                keyboardType="number-pad"
                error={inputErrors.branchCount?.message}
              />
            )}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.employee_count} />
          <Controller
            name="employeeCount"
            rules={{
              pattern: {
                value: patterns.number,
                message: strings.number_type_error,
              },
            }}
            control={control}
            render={({ field: { value, onChange } }) => (
              <Input
                editable={hasEditPermission}
                placeholder={strings.employee_count_sample}
                icon={{ name: "edit-2" }}
                onChangeText={onChange}
                value={value ? String(value) : undefined}
                keyboardType="number-pad"
                error={inputErrors.employeeCount?.message}
              />
            )}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.work_time} desc={` (${strings.year})`} />
          <Controller
            name="workTime"
            rules={{
              pattern: {
                value: patterns.number,
                message: strings.number_type_error,
              },
            }}
            control={control}
            render={({ field: { value, onChange } }) => (
              <Input
                editable={hasEditPermission}
                placeholder={strings.rental_sample}
                icon={{ name: "edit-2" }}
                onChangeText={onChange}
                value={value ? String(value) : undefined}
                keyboardType="number-pad"
                error={inputErrors.workTime?.message}
              />
            )}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.regal_count} />
          <Controller
            name="regalCount"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Input
                editable={hasEditPermission}
                placeholder={strings.regal_count_sample}
                icon={{ name: "edit-2" }}
                onChangeText={onChange}
                value={value ? String(value) : undefined}
                // keyboardType="number-pad"
                error={inputErrors.regalCount?.message}
              />
            )}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.role} />
          <Controller
            name="role"
            control={control}
            render={({ field: { value, onChange } }) => {
              return (
                <Input
                  editable={hasEditPermission}
                  value={value}
                  onChangeText={onChange}
                  icon={{ name: "user" }}
                  placeholder={strings.insert_customer_role}
                />
              );
            }}
          />
        </View>

        <View style={css.responsiveInputContainer}>
          <InputLabel label={strings.other_job} />
          <Controller
            name="otherJob"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Input
                editable={hasEditPermission}
                placeholder={strings.insert_other_job}
                icon={{ name: "edit-2" }}
                onChangeText={onChange}
                value={value}
              />
            )}
          />
        </View>

        <View style={{ ...css.fitWidth }}>
          <InputLabel label={strings.shop_descriptions} />
          <Controller
            name="shopDescriptions"
            control={control}
            render={({ field: { value, onChange } }) => (
              <TextArea
                editable={hasEditPermission}
                value={value}
                onChangeText={onChange}
                icon={{ name: "align-right" }}
                placeholder={strings.insert_shop_descriptions}
              />
            )}
          />
        </View>

        <View style={{ ...css.fitWidth }}>
          <InputLabel label={strings.popular_products} />
          <Controller
            control={control}
            name="popularProducts"
            render={({ field: { value, onChange } }) => {
              return (
                <PopularProducts
                  disabled={!hasEditPermission}
                  value={value}
                  onChange={onChange}
                />
              );
            }}
          />
        </View>

        <View style={{ ...css.fitWidth }}>
          <InputLabel label={strings.popular_categories} />
          <Controller
            control={control}
            name="popularCategories"
            render={({ field: { value, onChange } }) => {
              return (
                <PopularCategories
                  disabled={!hasEditPermission}
                  value={value}
                  onChange={onChange}
                />
              );
            }}
          />
        </View>

        <View style={{ ...css.fitWidth }}>
          <InputLabel label={strings.popular_attrs} />
          <Controller
            control={control}
            name="popularAttrs"
            render={({ field: { value, onChange } }) => (
              <PopularAttrs
                disabled={!hasEditPermission}
                onChange={onChange}
                attrs={value}
              />
            )}
          />
        </View>
      </ScrollView>

      {hasEditPermission && (
        <View
          style={{
            ...css.paddingH,
            ...css.rowCenterAlign,
            marginVertical: sizes.space / 2,
          }}
        >
          <View
            style={{
              ...css.container,
              maxWidth: sn("100%", "100%", 300, 300),
            }}
          >
            <Button
              loading={editCustomerMut.isLoading}
              style={css.selfCenter}
              title={{ value: strings.submit_info }}
              onPress={handleSubmit(_onEdit)}
            />
          </View>
          {data && chmod("customer", "DELETE") && (
            <View>
              <Button
                onPress={showAlert}
                square={mq(45)}
                bgColor={colors.danger}
                icon={{ name: "trash-2", size: 25 }}
                style={{ ...css.selfCenter, marginLeft: sizes.space }}
              />
            </View>
          )}
        </View>
      )}

      <ConfirmAlert
        force
        show={alert}
        hide={hideAlert}
        renderContent={(close) => (
          <DeleteAlertComponent
            removeLoading={deleteCustomerMut.isLoading}
            remove={() => _onDelete(close)}
            cancel={close}
          />
        )}
      />
    </>
  );
}
